/*
 * MesYat yazıcı adaptörü
 * - Epson ağ yazıcısı
 * - Android/Flutter yazıcı köprüsü
 * - Tarayıcı yazdırma
 */

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function normalizePayload(job) {
  const payload =
    job?.payload && typeof job.payload === "object"
      ? job.payload
      : {};

  return {
    queue_id: job?.id,
    ticket_no: job?.fis_no || payload.order_id || job?.siparis_id || "-",
    station: payload.station?.name || job?.istasyon_adi || "Mutfak",
    table: payload.table?.name || job?.masa_no || "Masa",
    waiter: payload.waiter?.name || job?.garson_adi || "-",
    note: payload.note || "",
    created_at:
      payload.created_at || job?.created_at || new Date().toISOString(),
    paper_width: Number(job?.kagit_genisligi) === 58 ? 58 : 80,
    printer_type:
      String(
        job?.yazici_turu ||
          payload?.printer?.type ||
          payload?.station?.printer_type ||
          "browser"
      ).toLowerCase(),
    printer_address:
      job?.yazici_adresi ||
      payload?.printer?.address ||
      payload?.printer?.ip ||
      payload?.station?.printer_address ||
      "",
    items: Array.isArray(payload.items) ? payload.items : [],
  };
}

function normalizePrinterAddress(value) {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "");
}

function formatDate(value) {
  try {
    return new Date(value).toLocaleString("tr-TR");
  } catch {
    return String(value || "");
  }
}

function ticketHtml(job) {
  const data = normalizePayload(job);
  const width = data.paper_width === 58 ? "54mm" : "76mm";

  const rows = data.items
    .map(
      (item) => `
        <div class="item">
          <strong>${escapeHtml(item.quantity || 1)} × ${escapeHtml(
            item.display_title || item.title || "Ürün"
          )}</strong>
          ${
            item.note
              ? `<div class="note">Not: ${escapeHtml(item.note)}</div>`
              : ""
          }
        </div>`
    )
    .join("");

  return `<!doctype html>
  <html lang="tr">
    <head>
      <meta charset="utf-8" />
      <title>MesYat Mutfak Fişi</title>
      <style>
        @page { size: ${data.paper_width}mm auto; margin: 2mm; }
        * { box-sizing: border-box; }
        body {
          width: ${width};
          margin: 0 auto;
          color: #000;
          font-family: Arial, sans-serif;
          font-size: 13px;
        }
        h1 {
          margin: 0 0 4px;
          text-align: center;
          font-size: 20px;
        }
        .meta {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 6px 0;
          line-height: 1.45;
        }
        .item {
          padding: 7px 0;
          border-bottom: 1px dashed #000;
          font-size: 15px;
        }
        .note {
          margin-top: 3px;
          font-size: 12px;
          font-weight: 700;
        }
        .footer {
          padding-top: 6px;
          text-align: center;
          font-size: 10px;
        }
      </style>
    </head>
    <body>
      <h1>${escapeHtml(data.station)}</h1>

      <div class="meta">
        <div><strong>${escapeHtml(data.table)}</strong></div>
        <div>Garson: ${escapeHtml(data.waiter)}</div>
        <div>Fiş: ${escapeHtml(data.ticket_no)}</div>
        <div>${escapeHtml(formatDate(data.created_at))}</div>
      </div>

      ${rows}

      ${
        data.note
          ? `<div class="item"><strong>Sipariş Notu:</strong><br>${escapeHtml(
              data.note
            )}</div>`
          : ""
      }

      <div class="footer">MesYat Dijital Garson & Adisyon</div>

      <script>
        window.onload = function () {
          window.print();
        };
      </script>
    </body>
  </html>`;
}



function receiptNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function receiptCurrency(value) {
  const currency = String(value || "TRY").toUpperCase();
  return currency === "TRY" || currency === "TL"
    ? "TL"
    : currency;
}

function receiptMoney(value, currency = "TRY") {
  return `${receiptNumber(value).toFixed(2).replace(".", ",")} ${receiptCurrency(
    currency
  )}`;
}

function receiptLine(left, right = "", width = 48) {
  const leftText = String(left || "");
  const rightText = String(right || "");

  if (!rightText) {
    return leftText.slice(0, width);
  }

  const maximumLeft = Math.max(1, width - rightText.length - 1);
  const fittedLeft = leftText.slice(0, maximumLeft);
  const spaces = Math.max(
    1,
    width - fittedLeft.length - rightText.length
  );

  return `${fittedLeft}${" ".repeat(spaces)}${rightText}`;
}

function epsonTicketXml(job) {
  const data = normalizePayload(job);
  const separator = "------------------------------------------------";

  let generalTotal = 0;
  let generalCurrency = "TRY";

  const itemXml = data.items
    .map((item) => {
      const quantity = Math.max(
        1,
        receiptNumber(item.quantity, 1)
      );

      const title =
        item.display_title ||
        item.title ||
        item.name ||
        "Ürün";

      const unitPrice = receiptNumber(
        item.price ??
          item.unit_price ??
          item.birim_fiyat ??
          item.fiyat,
        0
      );

      const currency =
        item.currency ||
        item.para_birimi ||
        generalCurrency;

      const lineTotal = receiptNumber(
        item.total ??
          item.total_price ??
          item.toplam ??
          item.toplam_fiyat,
        unitPrice * quantity
      );

      generalTotal += lineTotal;
      generalCurrency = currency;

      const productLine = `${quantity} x ${title}`;
      const priceLine = receiptLine(
        `Birim: ${receiptMoney(unitPrice, currency)}`,
        `Tutar: ${receiptMoney(lineTotal, currency)}`
      );

      return `
        <text em="true">${escapeXml(`${productLine}\n`)}</text>
        <text>${escapeXml(`${priceLine}\n`)}</text>
        ${
          item.note
            ? `<text>${escapeXml(`Ürün Notu: ${item.note}\n`)}</text>`
            : ""
        }
        <text>${escapeXml(`${separator}\n`)}</text>
      `;
    })
    .join("");

  const totalLine = receiptLine(
    "GENEL TOPLAM",
    receiptMoney(generalTotal, generalCurrency)
  );

  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
  <s:Body>
    <epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">

      <text align="center" em="true">${escapeXml(
        `${data.station}\n`
      )}</text>

      <text align="center">MESYAT DİJİTAL SİPARİŞ FİŞİ\n</text>

      <feed line="1"/>

      <text align="left">${escapeXml(
        `${receiptLine("Masa", data.table)}\n` +
          `${receiptLine("Garson", data.waiter)}\n` +
          `${receiptLine("Fiş No", data.ticket_no)}\n` +
          `${receiptLine("Tarih", formatDate(data.created_at))}\n`
      )}</text>

      <text>${escapeXml(`${separator}\n`)}</text>

      ${itemXml}

      ${
        data.note
          ? `<text em="true">${escapeXml(
              `Sipariş Notu: ${data.note}\n`
            )}</text>
             <text>${escapeXml(`${separator}\n`)}</text>`
          : ""
      }

      <text em="true">${escapeXml(`${totalLine}\n`)}</text>

      <feed line="2"/>

      <text align="center" em="true">MesYat Dijital\n</text>
      <text align="center">Akıllı Sipariş ve Adisyon Sistemi\n</text>
      <text align="center">panel.haymestasarim.com\n</text>

      <feed line="3"/>
      <cut type="feed"/>

    </epos-print>
  </s:Body>
</s:Envelope>`;
}
export function hasMesYatNativePrinter() {
  if (typeof window === "undefined") return false;

  return Boolean(
    window.MesYatPrinter?.printTicket ||
      window.MesYatPrinter?.postMessage
  );
}

export function hasMesYatNetworkPrinter(jobOrStation) {
  const data = normalizePayload(jobOrStation);

  const printerType = String(
    jobOrStation?.yazici_turu ||
      jobOrStation?.printer_type ||
      data.printer_type ||
      ""
  ).toLowerCase();

  const address = normalizePrinterAddress(
    jobOrStation?.yazici_adresi ||
      jobOrStation?.printer_address ||
      data.printer_address
  );

  return printerType === "network" && address.length > 0;
}

async function printEpsonNetworkTicket(job) {
  const data = normalizePayload(job);
  const address = normalizePrinterAddress(data.printer_address);

  if (!address) {
    throw new Error("Epson yazıcı IP adresi tanımlanmamış.");
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20000);

  const endpoint =
  `http://${address}/cgi-bin/epos/service.cgi` +
  `?devid=local_printer&timeout=15000`;

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "text/xml; charset=utf-8",
        SOAPAction: '""',
      },
      body: epsonTicketXml(job),
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(
        `Epson bağlantı hatası: HTTP ${response.status}`
      );
    }

    const success =
      /success\s*=\s*["']true["']/i.test(responseText);

    if (!success) {
      const code =
        responseText.match(/code\s*=\s*["']([^"']+)["']/i)?.[1] ||
        "EPSON_PRINT_ERROR";

      throw new Error(`Epson çıktıyı kabul etmedi: ${code}`);
    }

    return {
      mode: "network",
      ok: true,
      confirmed: true,
      address,
    };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("Epson yazıcı 20 saniye içinde cevap vermedi.");
    }

 if (error instanceof TypeError) {
  throw new Error(
    `Epson yazıcıya bağlanılamadı (${address}). Bilgisayar ve yazıcı aynı ağda olmalı; MesYat yazıcı kurulumu bu bilgisayarda bir kez çalıştırılmalıdır.`
  );
}

    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function printKitchenTicket(job) {
  const data = normalizePayload(job);

  if (data.printer_type === "network") {
    return printEpsonNetworkTicket(job);
  }

  if (typeof window?.MesYatPrinter?.printTicket === "function") {
    await window.MesYatPrinter.printTicket(data);

    return {
      mode: "native",
      ok: true,
      confirmed: true,
    };
  }

  if (typeof window?.MesYatPrinter?.postMessage === "function") {
    window.MesYatPrinter.postMessage(
      JSON.stringify({
        action: "print_ticket",
        payload: data,
      })
    );

    return {
      mode: "native_channel",
      ok: true,
      confirmed: true,
    };
  }

  const printWindow = window.open(
    "",
    "_blank",
    "width=480,height=760"
  );

  if (!printWindow) {
    throw new Error(
      "Yazdırma penceresi engellendi. Tarayıcıda açılır pencerelere izin verin."
    );
  }

  printWindow.document.open();
  printWindow.document.write(ticketHtml(job));
  printWindow.document.close();

  return {
    mode: "browser",
    ok: true,
    confirmed: false,
  };
}

export default {
  hasMesYatNativePrinter,
  hasMesYatNetworkPrinter,
  printKitchenTicket,
};